/**
 * Formatação única de datas da Auditoria da Prestação de Contas.
 *
 * O backend responde data-hora em ISO 8601 completo, com fração de segundo e
 * fuso ("2026-10-17T14:59:14.965308-03:00"), além de data civil ("2026-10-17")
 * e competência ("2026-10"). Formatar isso com `split('-')` produz lixo na tela
 * ("17T14:59:14.965308/10/2026"), então TODA data da auditoria passa por aqui.
 *
 * Regras:
 *  - data civil (AAAA-MM-DD) é montada no calendário local, sem deslocar o dia
 *    por fuso — 17/10 continua 17/10 em qualquer máquina;
 *  - data-hora COM fuso é exibida no horário de Brasília (Goiânia);
 *  - data-hora SEM fuso é exibida como veio (relógio de parede do backend);
 *  - competência (AAAA-MM) vira "Out/2026";
 *  - valor nulo ou ilegível devolve "Não informado" — nunca a string crua.
 */

const MESES_ABREVIADOS = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'];

const FUSO_BR = 'America/Sao_Paulo';

/** Texto exibido quando não há data legível. */
export const DATA_NAO_INFORMADA = 'Não informado';

export type ValorData = string | null | undefined;

const RE_DATA = /^(\d{4})-(\d{2})-(\d{2})$/;
const RE_COMPETENCIA = /^(\d{4})-(\d{2})$/;
const RE_DATA_HORA =
  /^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})(?::(\d{2})(?:\.(\d{1,9}))?)?(Z|[+-]\d{2}:?\d{2})?$/i;

function doisDigitos(valor: number): string {
  return String(valor).padStart(2, '0');
}

/** Data civil real? Rejeita 2026-02-31 e mês 13 sem inventar normalização. */
function dataCivilValida(ano: number, mes: number, dia: number): boolean {
  if (mes < 1 || mes > 12 || dia < 1 || dia > 31) return false;
  const d = new Date(ano, mes - 1, dia);
  return d.getFullYear() === ano && d.getMonth() === mes - 1 && d.getDate() === dia;
}

/**
 * "14:59" no horário de Brasília, a partir de um instante absoluto.
 * Usa Intl com hora 0–23 (h23) para não aparecer "24:00" à meia-noite.
 */
function dataHoraEmBrasilia(d: Date): string {
  try {
    const partes = new Intl.DateTimeFormat('pt-BR', {
      timeZone: FUSO_BR,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hourCycle: 'h23',
    }).formatToParts(d);
    const valor = (tipo: string): string => partes.find((p) => p.type === tipo)?.value ?? '';
    const dia = valor('day');
    const mes = valor('month');
    const ano = valor('year');
    const hora = valor('hour');
    const minuto = valor('minute');
    if (dia && mes && ano && hora && minuto) return `${dia}/${mes}/${ano} ${hora}:${minuto}`;
  } catch {
    // Sem Intl/tz disponível: cai no relógio local logo abaixo.
  }
  return `${doisDigitos(d.getDate())}/${doisDigitos(d.getMonth() + 1)}/${d.getFullYear()} ${doisDigitos(d.getHours())}:${doisDigitos(d.getMinutes())}`;
}

/** Data-hora no relógio de parede local (usada quando o ISO não traz fuso). */
function dataHoraLocal(d: Date): string {
  return `${doisDigitos(d.getDate())}/${doisDigitos(d.getMonth() + 1)}/${d.getFullYear()} ${doisDigitos(d.getHours())}:${doisDigitos(d.getMinutes())}`;
}

/**
 * Frações longas (ex.: .965308) não fazem parte do formato estrito do
 * ECMAScript. Truncar para milissegundos evita "Invalid Date" em navegadores
 * mais rígidos, sem perder nada do que é exibido (minutos).
 */
function normalizarIso(bruto: string): string {
  return bruto
    .replace(' ', 'T')
    .replace(/\.(\d{3})\d+/, '.$1')
    .replace(/([+-]\d{2})(\d{2})$/, '$1:$2');
}

/**
 * Formata data-hora, data civil ou competência em pt-BR.
 *
 *  - "2026-10-17T14:59:14.965308-03:00" -> "17/10/2026 14:59"
 *  - "2026-10-17"                      -> "17/10/2026"
 *  - "2026-10"                         -> "Out/2026"
 *  - nulo / ilegível                   -> "Não informado"
 */
export function formatarDataHoraBR(valor: ValorData): string {
  if (valor === null || valor === undefined) return DATA_NAO_INFORMADA;
  const bruto = String(valor).trim();
  if (!bruto) return DATA_NAO_INFORMADA;

  const dataHora = RE_DATA_HORA.exec(bruto);
  if (dataHora) {
    const comFuso = Boolean(dataHora[8]);
    const d = new Date(normalizarIso(bruto));
    if (Number.isNaN(d.getTime())) return DATA_NAO_INFORMADA;
    return comFuso ? dataHoraEmBrasilia(d) : dataHoraLocal(d);
  }

  const data = RE_DATA.exec(bruto);
  if (data) {
    const ano = Number(data[1]);
    const mes = Number(data[2]);
    const dia = Number(data[3]);
    if (!dataCivilValida(ano, mes, dia)) return DATA_NAO_INFORMADA;
    return `${doisDigitos(dia)}/${doisDigitos(mes)}/${ano}`;
  }

  const competencia = RE_COMPETENCIA.exec(bruto);
  if (competencia) {
    const mes = Number(competencia[2]);
    if (mes < 1 || mes > 12) return DATA_NAO_INFORMADA;
    return `${MESES_ABREVIADOS[mes - 1]}/${competencia[1]}`;
  }

  return DATA_NAO_INFORMADA;
}

/**
 * Somente a data (dd/mm/aaaa) — para campos em que a hora não interessa
 * (última entrega recebida, vigência, prazo). Competência continua "Out/2026".
 */
export function formatarDataBR(valor: ValorData): string {
  const formatado = formatarDataHoraBR(valor);
  if (formatado === DATA_NAO_INFORMADA) return formatado;
  return formatado.split(' ')[0];
}

/**
 * Competência "AAAA-MM" (ou uma data cujo mês é a competência) -> "Out/2026".
 * Valor nulo/ilegível devolve "Não informado".
 */
export function formatarCompetenciaBR(valor: ValorData): string {
  if (valor === null || valor === undefined) return DATA_NAO_INFORMADA;
  const bruto = String(valor).trim();
  if (!bruto) return DATA_NAO_INFORMADA;
  const prefixo = /^(\d{4})-(\d{2})/.exec(bruto);
  if (!prefixo) return DATA_NAO_INFORMADA;
  const mes = Number(prefixo[2]);
  if (mes < 1 || mes > 12) return DATA_NAO_INFORMADA;
  return `${MESES_ABREVIADOS[mes - 1]}/${prefixo[1]}`;
}

/**
 * Data para exibir com toLocale*: a data civil 'AAAA-MM-DD' é montada no calendário LOCAL. Com
 * `new Date('2025-09-01')` o navegador lê meia-noite UTC e, em Goiânia (UTC−3), mostra 31/08.
 */
export function paraData(valor: unknown): Date {
  if (valor instanceof Date) return valor;
  if (typeof valor === 'number') return new Date(valor);
  const bruto = String(valor ?? '').trim();
  const civil = /^(\d{4})-(\d{2})-(\d{2})$/.exec(bruto);
  if (civil) return new Date(Number(civil[1]), Number(civil[2]) - 1, Number(civil[3]));
  return new Date(bruto);
}
